import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listGoogleAccounts, disconnectGoogleAccount, type GoogleAccount } from '../../services/google-account-api.js';

@customElement('cp-google-accounts-settings')
export class CpGoogleAccountsSettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _accounts: GoogleAccount[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _connectAlias = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._loadAccounts();

    // Check URL params for OAuth callback status
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true') {
      this._loadAccounts();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    const googleError = params.get('google_error');
    if (googleError) {
      this._error = `Google-Verbindung fehlgeschlagen: ${googleError}`;
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  private async _loadAccounts(): Promise<void> {
    this._loading = true;
    try {
      this._accounts = await listGoogleAccounts();
      this._error = '';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden';
    } finally {
      this._loading = false;
    }
  }

  private _handleConnect(): void {
    const alias = this._connectAlias.trim();
    if (!alias) return;
    // Navigate to OAuth flow
    window.location.href = `/api/google-accounts/connect?alias=${encodeURIComponent(alias)}`;
  }

  private async _handleDisconnect(account: GoogleAccount): Promise<void> {
    if (!confirm(`Google-Account "${account.account_alias}" (${account.google_email}) trennen?`)) return;
    try {
      await disconnectGoogleAccount(account.id);
      await this._loadAccounts();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Trennen';
    }
  }

  private _handleReconnect(account: GoogleAccount): void {
    window.location.href = `/api/google-accounts/connect?alias=${encodeURIComponent(account.account_alias)}`;
  }

  render() {
    return html`
      <div class="cp-google-accounts">
        <h3 class="cp-google-accounts__title">Google Accounts</h3>
        <p class="cp-google-accounts__desc">Verbinde Google-Accounts fuer Gmail, Drive, Calendar und Sheets.</p>

        ${this._error ? html`<p style="color: var(--color-danger, #ff3b30); font-size: var(--font-size-sm);">${this._error}</p>` : nothing}

        ${this._loading ? html`<p class="cp-google-accounts__empty">Laden...</p>` : html`
          <div class="cp-google-accounts__list">
            ${this._accounts.length === 0
              ? html`<p class="cp-google-accounts__empty">Noch keine Google-Accounts verbunden.</p>`
              : this._accounts.map((account) => html`
                <div class="cp-google-accounts__item">
                  <span class="cp-google-accounts__status-dot ${account.needs_reauth ? 'cp-google-accounts__status-dot--reauth' : 'cp-google-accounts__status-dot--ok'}"></span>
                  <div class="cp-google-accounts__item-info">
                    <div class="cp-google-accounts__item-alias">${account.account_alias}</div>
                    <div class="cp-google-accounts__item-email">${account.google_email}</div>
                  </div>
                  <div class="cp-google-accounts__item-actions">
                    ${account.needs_reauth ? html`
                      <button class="cp-google-accounts__btn" @click=${() => this._handleReconnect(account)}>Erneut verbinden</button>
                    ` : nothing}
                    <button class="cp-google-accounts__btn cp-google-accounts__btn--danger" @click=${() => this._handleDisconnect(account)}>Trennen</button>
                  </div>
                </div>
              `)
            }
          </div>

          <div class="cp-google-accounts__connect">
            <input
              class="cp-google-accounts__connect-input"
              type="text"
              placeholder="Alias (z.B. private, rockstar)"
              .value=${this._connectAlias}
              @input=${(e: Event) => { this._connectAlias = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._handleConnect(); }}
            />
            <button class="cp-google-accounts__btn cp-google-accounts__btn--primary" @click=${this._handleConnect}>
              Account verbinden
            </button>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-google-accounts-settings': CpGoogleAccountsSettings;
  }
}
